/** URL/query helpers for Срочное — risk filter + severity (высокий / высокий+средний). */

import {
  URGENT_DEFAULT_DEAD_DEVICE_MONTHS,
  URGENT_DEAD_DEVICE_MONTH_OPTIONS,
  formatDeadDeviceMonthOption,
} from '../constants/deadDeviceThreshold';

export { URGENT_DEAD_DEVICE_MONTH_OPTIONS, formatDeadDeviceMonthOption };

export const URGENT_RISK_OPTIONS = [
  { id: 'all', label: 'Всё' },
  { id: '1', label: 'Риск №1 · Нет связи' },
  { id: '2', label: 'Риск №2 · Показания' },
  { id: '3', label: 'Риск №3 · Слабый сигнал' },
  { id: '4', label: 'Риск №4 · Батарея' },
];

export const URGENT_SEVERITY_OPTIONS = [
  { id: 'very_high', label: 'Очень высокий' },
  { id: 'high', label: 'Высокий' },
  { id: 'high_medium', label: 'Высокий и средний' },
];

export const URGENT_RISK_LABELS = {
  1: 'Связь',
  2: 'Показания',
  3: 'Сигнал',
  4: 'Батарея',
};

export function selectedUrgentRisk(value) {
  return URGENT_RISK_OPTIONS.some((option) => option.id === value) ? value : 'all';
}

export function selectedUrgentSeverity(value) {
  return URGENT_SEVERITY_OPTIONS.some((option) => option.id === value) ? value : 'very_high';
}

export function selectedUrgentDeadMonths(value) {
  const parsed = Number.parseInt(value, 10);
  return URGENT_DEAD_DEVICE_MONTH_OPTIONS.includes(parsed)
    ? String(parsed)
    : String(URGENT_DEFAULT_DEAD_DEVICE_MONTHS);
}

export function isAllUrgentRisks(risk) {
  return !risk || risk === 'all';
}

/** Full type line shown on an urgent card, including a single-risk filter. */
export function urgentRiskTypeLabel(riskNumber) {
  const id = String(riskNumber || '');
  const option = URGENT_RISK_OPTIONS.find((row) => row.id === id);
  return option ? option.label : '';
}

export function urgentSeverityQuery(severity, deadDeviceMonths) {
  const id = selectedUrgentSeverity(severity);
  const months = selectedUrgentDeadMonths(deadDeviceMonths);
  return `severity=${encodeURIComponent(id)}&dead_device_months=${encodeURIComponent(months)}`;
}

export function urgentPathForRisk(risk, severity, deadDeviceMonths) {
  const q = urgentSeverityQuery(severity, deadDeviceMonths);
  if (risk === '2') {
    return `/api/firmware/readings-urgent?${q}`;
  }
  if (risk === '3') {
    return `/api/firmware/signal-urgent?${q}`;
  }
  if (risk === '4') {
    return `/api/firmware/battery-urgent?${q}`;
  }
  return `/api/firmware/offline-send-alert?${q}`;
}

export function urgentFetchJobs(risk, severity, deadDeviceMonths) {
  if (isAllUrgentRisks(risk)) {
    return [
      { riskNumber: 1, path: urgentPathForRisk('1', severity, deadDeviceMonths), label: 'Offline send alert' },
      { riskNumber: 2, path: urgentPathForRisk('2', severity, deadDeviceMonths), label: 'Readings urgent' },
      { riskNumber: 3, path: urgentPathForRisk('3', severity, deadDeviceMonths), label: 'Signal urgent' },
      { riskNumber: 4, path: urgentPathForRisk('4', severity, deadDeviceMonths), label: 'Battery urgent' },
    ];
  }
  const riskNumber = Number(risk);
  return [
    {
      riskNumber,
      path: urgentPathForRisk(risk, severity, deadDeviceMonths),
      label:
        risk === '2'
          ? 'Readings urgent'
          : risk === '3'
            ? 'Signal urgent'
            : risk === '4'
              ? 'Battery urgent'
              : 'Offline send alert',
    },
  ];
}

/** Eisenhower buckets for Срочное. Dropped items are omitted. */
export const URGENT_QUADRANTS = [
  { id: 'now', title: 'Сделать сейчас', hint: 'A · поддержка (колл / акт / замена по гарантии)' },
  { id: 'plan', title: 'Запланировать', hint: 'B · очередь поддержки' },
  { id: 'delegate', title: 'Авто клиенту', hint: 'C · push/email/SMS, не колл-лист' },
];

function urgentItemLevel(item) {
  if (item?.level) {
    return String(item.level);
  }
  const days = Number(item?.daysLeft);
  if (!Number.isFinite(days)) {
    return '';
  }
  if (days <= 0) return 'very_high';
  if (days <= 2) return 'high';
  if (days <= 5) return 'medium';
  return '';
}

/**
 * Prefer backend Eisenhower `decision.quadrant` (eisenhower_v1).
 * Legacy fallback only if decision missing:
 * now — risk 1 within 2 days, or risk 2; plan — risk 1 in 3–5 days / high battery;
 * delegate — high signal while readings still go out.
 */
export function urgentQuadrant(item) {
  const q = item?.decision?.quadrant;
  if (q === 'A') return 'now';
  if (q === 'B') return 'plan';
  if (q === 'C') return 'delegate';
  if (q === 'D') return null;
  // needs_review / suppressed / missing → do not invent support A from score alone
  if (item?.decision?.decision_status && item.decision.decision_status !== 'determined') {
    return null;
  }

  const risk = Number(item?.riskNumber);
  const level = urgentItemLevel(item);
  if (risk === 1) {
    const days = Number(item.daysLeft);
    if (!Number.isFinite(days)) return null;
    if (days <= 2) return 'now';
    if (days <= 5) return 'plan';
    return null;
  }
  if (risk === 2) {
    // Legacy: all risk2 → now. With decision attached, human-data C already mapped above.
    return 'now';
  }
  if (risk === 3) {
    return level === 'very_high' || level === 'high' ? 'delegate' : null;
  }
  if (risk === 4) {
    return level === 'very_high' || level === 'high' ? 'plan' : null;
  }
  return null;
}

export function groupUrgentItems(items) {
  const groups = { now: [], plan: [], delegate: [] };
  for (const item of items || []) {
    const quadrant = urgentQuadrant(item);
    if (quadrant && groups[quadrant]) {
      groups[quadrant].push(item);
    }
  }
  return groups;
}

/** Sort key for merged «Всё» list — higher first. */
export function urgentSortScore(item) {
  if (Number.isFinite(Number(item.score))) {
    return Number(item.score);
  }
  const days = Number(item.daysLeft);
  if (!Number.isFinite(days)) {
    return 0;
  }
  if (days <= 0) return 100;
  if (days === 1) return 80;
  if (days === 2) return 65;
  if (days === 3) return 45;
  if (days === 4) return 35;
  return 25;
}

export function mergeUrgentItems(payloads) {
  const items = [];
  for (const payload of payloads || []) {
    const riskNumber = Number(payload.riskNumber) || 0;
    for (const item of payload.items || []) {
      if (item.status === 'resolved') {
        continue;
      }
      items.push({
        ...item,
        riskNumber: item.riskNumber || riskNumber,
        riskLabel: urgentRiskTypeLabel(item.riskNumber || riskNumber),
      });
    }
  }
  items.sort((left, right) => {
    const scoreDiff = urgentSortScore(right) - urgentSortScore(left);
    if (scoreDiff !== 0) {
      return scoreDiff;
    }
    const fioLeft = String(left.fio || '');
    const fioRight = String(right.fio || '');
    return fioLeft.localeCompare(fioRight, 'ru');
  });
  return items;
}
